import { useCallback, useEffect, useState } from 'react';
import { procurementApi } from '@/api/procurement.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { usePurchaseOrderOptions } from '@/hooks/usePurchaseOrderOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

type GrnRow = {
  id: number;
  grnNumber: string;
  grnStatus: string;
  posted: boolean;
  purchaseOrderId: number;
};

export function GoodsReceiptsPage() {
  const { hasAny } = usePermissions();
  const canReceive = hasAny('PROCUREMENT_RECEIVE', 'PROCUREMENT_MANAGE');
  const { getLabel: getPurchaseOrderLabel } = usePurchaseOrderOptions();
  const pagination = usePagination();
  const [items, setItems] = useState<GrnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await procurementApi.listGoodsReceipts({
        page: pagination.page,
        size: pagination.size,
      });
      setItems(page.content);
      pagination.applyPageResponse(page);
    } catch (e) {
      showApiError(e, 'Failed to load GRNs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const rejectQc = async (id: number) => {
    setBusyId(id);
    try {
      const grn = await procurementApi.rejectGrnQc(id, 'Failed inspection');
      setItems((prev) => prev.map((g) => (g.id === id ? { ...g, ...grn } : g)));
      notify.success('QC rejected — stock will not increase');
    } catch (err) {
      showApiError(err, 'QC rejection failed');
    } finally {
      setBusyId(null);
    }
  };

  const qcApprove = async (id: number) => {
    setBusyId(id);
    try {
      const grn = await procurementApi.approveGrnQc(id);
      setItems((prev) => prev.map((g) => (g.id === id ? { ...g, ...grn } : g)));
      notify.success('QC approved');
    } catch (err) {
      showApiError(err, 'QC approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const postGrn = async (id: number) => {
    setBusyId(id);
    try {
      const grn = await procurementApi.postGoodsReceipt(id);
      setItems((prev) => prev.map((g) => (g.id === id ? { ...g, ...grn } : g)));
      notify.success(`GRN ${grn.grnNumber} posted — inventory updated`);
    } catch (err) {
      showApiError(err, 'GRN post failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Goods receipts"
        description="QC approve then post GRN to increase inventory"
        breadcrumbs={[{ label: 'Procurement', to: '/procurement' }, { label: 'Goods receipts' }]}
      />
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left dark:border-slate-700">
              <th className="p-3">GRN</th>
              <th className="p-3">Status</th>
              <th className="p-3">PO</th>
              <th className="p-3">Posted</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((grn) => (
              <tr key={grn.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3 font-medium">{grn.grnNumber}</td>
                <td className="p-3">{grn.grnStatus}</td>
                <td className="p-3">{getPurchaseOrderLabel(grn.purchaseOrderId)}</td>
                <td className="p-3">{grn.posted ? 'Yes' : 'No'}</td>
                <td className="p-3 flex gap-2">
                  {canReceive && !grn.posted && grn.grnStatus === 'DRAFT' && (
                    <>
                      <Button loading={busyId === grn.id} onClick={() => void qcApprove(grn.id)}>
                        QC approve
                      </Button>
                      <Button variant="secondary" loading={busyId === grn.id} onClick={() => void rejectQc(grn.id)}>
                        QC reject
                      </Button>
                    </>
                  )}
                  {canReceive && !grn.posted && grn.grnStatus === 'QC_APPROVED' && (
                    <Button loading={busyId === grn.id} onClick={() => void postGrn(grn.id)}>
                      Post GRN
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={items.length > 0}
        />
      </div>
    </div>
  );
}
